import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useConsent } from "@/lib/consent";
import { langOfPath } from "@/locale/alternates";
import { FOOTER_EN } from "@/en/nav";
import { FOOTER_NL } from "@/nl/nav";

const WA_HREF = "https://wa.me/4915678575025?text=Hallo%2C%20ich%20interessiere%20mich%20f%C3%BCr%20den%20Kauf%20einer%20Fotobox%20und%20h%C3%A4tte%20hierzu%20eine%20Frage.";

// Kleine Linkliste zu den Regionen-Seiten (SEO, siehe lib/regions.js)
const REGION_LINKS = [
  ["deutschland", "Deutschland"], ["oesterreich", "Österreich"], ["schweiz", "Schweiz"],
  ["berlin", "Berlin"], ["hamburg", "Hamburg"], ["muenchen", "München"], ["koeln", "Köln"],
  ["frankfurt", "Frankfurt"], ["essen", "Essen"], ["wien", "Wien"], ["zuerich", "Zürich"],
];

export default function Footer() {
  const { openSettings } = useConsent();
  const { pathname } = useLocation();
  const lang = langOfPath(pathname);
  if (lang === "en") return <FooterIntl f={FOOTER_EN} openSettings={openSettings} />;
  if (lang === "nl") return <FooterIntl f={FOOTER_NL} openSettings={openSettings} />;
  return (
    <footer className="bg-cream border-t border-hair pt-20 pb-10 mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="font-heading text-lg font-semibold text-ink mb-1 leading-none">
              fotoboxverkauf<span className="text-brand">.de</span>
            </div>
            <div className="text-[11px] tracking-wide text-ink-muted font-medium mb-4">
              Kreul Media Group
            </div>
            <p className="text-ink-soft text-sm leading-relaxed max-w-xs">
              Professioneller deutscher Fachhandel für Fotobox-Systeme. Showroom in Bottrop-Fuhlenbrock im Ruhrgebiet.
            </p>
          </div>
          <div>
            <div className="eyebrow mb-4">Sortiment</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li><Link to="/fotoboxen" className="hover:text-brand">Alle Modelle</Link></li>
              <li><Link to="/vergleich" className="hover:text-brand">Modelle vergleichen</Link></li>
              <li><Link to="/produktfinder" className="hover:text-brand">Produktfinder</Link></li>
              <li><Link to="/kreulbooth" className="hover:text-brand" data-testid="footer-kreulbooth">KreulBooth Software</Link></li>
              <li><Link to="/zubehoer" className="hover:text-brand">Zubehör</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4">Service</div>
            <ul className="space-y-2 text-sm text-ink-soft">
              <li><Link to="/fotobox-pro-academy" className="hover:text-brand font-semibold text-ink" data-testid="footer-academy">Fotobox Academy Pro</Link></li>
              <li><Link to="/showroom" className="hover:text-brand">Showroom-Termin</Link></li>
              <li><Link to="/service" className="hover:text-brand">Service &amp; Support</Link></li>
              <li><Link to="/faq" className="hover:text-brand">Häufige Fragen</Link></li>
              <li><Link to="/ueber-uns" className="hover:text-brand">Über uns</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4">Kontakt</div>
            <ul className="space-y-3 text-sm text-ink-soft">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <span>
                  fotoboxverkauf.de<br />
                  Im Fuhlenbrock 113<br />
                  46242 Bottrop
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-brand" />
                <a href="tel:020417355679" className="hover:text-brand" data-testid="footer-phone">02041 7355679</a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="w-4 h-4 mt-0.5 text-brand" />
                <a href={WA_HREF} target="_blank" rel="noreferrer" className="hover:text-brand" data-testid="footer-whatsapp">
                  WhatsApp: 0156 78575025
                </a>
              </li>
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-brand" /> <a href="mailto:info@fotoboxverkauf.de" className="hover:text-brand">info@fotoboxverkauf.de</a></li>
            </ul>
          </div>
        </div>
        <nav className="mb-8 text-xs text-ink-muted flex flex-wrap items-center gap-x-3 gap-y-2" aria-label="Fotobox kaufen in" data-testid="footer-regions">
          <span className="font-semibold text-ink-soft">Fotobox kaufen in:</span>
          {REGION_LINKS.map(([slug, name]) => (
            <Link key={slug} to={`/fotobox-kaufen/${slug}`} className="hover:text-brand py-1">{name}</Link>
          ))}
        </nav>
        <div className="divider-soft mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-ink-muted">
          <div className="flex items-center gap-3 flex-wrap justify-center md:justify-start">
            <span>© {new Date().getFullYear()} fotoboxverkauf.de: Fachhandel für Fotobox-Systeme.</span>
            <span className="text-ink-muted/40">·</span>
            <Link
              to="/login"
              data-testid="footer-admin-login"
              className="underline underline-offset-2 hover:text-brand"
            >
              Mitarbeiter-Login
            </Link>
          </div>
          <div className="flex gap-6 items-center flex-wrap justify-center">
            <Link to="/faq" className="hover:text-brand">FAQ</Link>
            <Link to="/impressum" className="hover:text-brand" data-testid="footer-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-brand" data-testid="footer-datenschutz">Datenschutz</Link>
            <Link to="/agb" className="hover:text-brand" data-testid="footer-agb">AGB</Link>
            <Link to="/gewaehrleistung" className="hover:text-brand" data-testid="footer-guarantee">Gesetzliche Gewährleistung</Link>
            <Link to="/widerrufsbelehrung" className="hover:text-brand" data-testid="footer-widerruf">Widerrufsbelehrung</Link>
            <Link to="/kuendigen" className="hover:text-brand font-semibold" data-testid="footer-kuendigen">Verträge hier kündigen</Link>
            <Link to="/widerrufen" className="hover:text-brand-yellow font-semibold" data-testid="footer-vertrag-widerrufen">Vertrag widerrufen</Link>
            <button type="button" onClick={openSettings} className="hover:text-brand underline underline-offset-2" data-testid="footer-cookie-settings">Cookie-Einstellungen</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Fuß der Sprachbereiche /en und /nl (Texte in en/nav.js bzw. nl/nav.js). */
function FooterIntl({ f, openSettings }) {
  return (
    <footer className="bg-cream border-t border-hair pt-20 pb-10 mt-24" data-testid={f.testid}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="font-heading text-lg font-semibold text-ink mb-1 leading-none">fotoboxverkauf<span className="text-brand">.de</span></div>
            <div className="text-[11px] tracking-wide text-ink-muted font-medium mb-4">Kreul Media Group</div>
            <p className="text-ink-soft text-sm leading-relaxed max-w-xs">{f.tagline}</p>
          </div>
          {f.columns.map((col) => (
            <div key={col.title}>
              <div className="eyebrow mb-4">{col.title}</div>
              <ul className="space-y-2 text-sm text-ink-soft">
                {col.links.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-brand">{l.label}</Link></li>)}
              </ul>
            </div>
          ))}
          <div>
            <div className="eyebrow mb-4">{f.contactTitle}</div>
            <ul className="space-y-3 text-sm text-ink-soft">
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-brand shrink-0" /><span>{f.address.map((line, i) => <React.Fragment key={line}>{i > 0 && <br />}{line}</React.Fragment>)}</span></li>
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-brand" /><a href="tel:+4920417355679" className="hover:text-brand">+49 2041 7355679</a></li>
              <li className="flex items-start gap-3"><MessageCircle className="w-4 h-4 mt-0.5 text-brand" /><a href={f.whatsappHref} target="_blank" rel="noreferrer" className="hover:text-brand">WhatsApp: +49 156 78575025</a></li>
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-brand" /> <a href="mailto:info@fotoboxverkauf.de" className="hover:text-brand">info@fotoboxverkauf.de</a></li>
            </ul>
          </div>
        </div>
        <div className="divider-soft mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-ink-muted">
          <span>© {new Date().getFullYear()} {f.copyright}</span>
          <div className="flex gap-6 items-center flex-wrap justify-center">
            {f.legal.map((l) => <Link key={l.to} to={l.to} className="hover:text-brand">{l.label}</Link>)}
            <button type="button" onClick={openSettings} className="hover:text-brand underline underline-offset-2">{f.cookies}</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
